import type { CustomerToolOption } from './ToolSelector';

/**
 * **Werkzeuge per @ im Chat ansprechen — die reinen Teile.**
 *
 * Wunsch von Alexander Tiegs (Meeting 06.10.2026): ein Werkzeug direkt im Chat nennen, auch
 * mehrere. Der **Text ist die Wahrheit**: Gewählt ist, was als `@Name` im Feld steht. Die Chips
 * über dem Feld spiegeln das nur — wer `@Name` löscht oder einfügt, ändert die Wahl, ohne dass ein
 * zweiter Zustand auseinanderlaufen kann. Werkzeugnamen tragen Leerzeichen
 * („PowerPoint-Generierung und -Bearbeitung"), deshalb wird gegen die bekannten Namen gesucht und
 * nicht bis zum nächsten Leerzeichen gelesen.
 */

/** Wie weit hinter dem `@` noch gesucht wird; längere Namen gibt es im Bestand nicht. */
const MAX_QUERY_LENGTH = 60;

export const MAX_MENTION_CANDIDATES = 8;

export type ActiveMention = { start: number; query: string };

const isBoundary = (char: string | undefined) => char === undefined || /[\s.,;:!?)\]}"'»“]/.test(char);

/**
 * Das `@…`, in dem die Schreibmarke gerade steht — oder `null`. Ein `@` mitten in einem Wort
 * (eine E-Mail-Adresse) zählt nicht, ein Zeilenumbruch beendet die Suche.
 */
export function activeMentionQuery(text: string, caret: number): ActiveMention | null {
    const before = text.slice(0, caret);
    const at = before.lastIndexOf('@');
    if (at < 0) return null;
    if (at > 0 && !/\s/.test(before[at - 1])) return null;
    const query = before.slice(at + 1);
    if (query.length > MAX_QUERY_LENGTH || query.includes('\n')) return null;
    return { start: at, query };
}

/**
 * Passende Werkzeuge für das Getippte: erst die, deren Name damit beginnt, dann die, in denen ein
 * Wort damit beginnt, dann die, die es irgendwo enthalten — darin alphabetisch, wie die Liste.
 * Ein Name, der schon vollständig dasteht, öffnet die Liste nicht wieder — abgeschlossen von einem
 * Leerzeichen nie, ohne nur dann, wenn kein längerer Name damit beginnt. Sonst übernähme Enter nach
 * einem von Hand getippten `@Websuche` nur, statt zu senden.
 */
export function matchMentionCandidates(
    query: string,
    tools: CustomerToolOption[],
    limit = MAX_MENTION_CANDIDATES,
): CustomerToolOption[] {
    const q = query.toLocaleLowerCase();
    if (/\s$/.test(q) && tools.some((tool) => tool.name.toLocaleLowerCase() === q.trimEnd())) return [];
    const rank = (name: string): number => {
        const n = name.toLocaleLowerCase();
        if (n.startsWith(q)) return 0;
        if (n.split(/[\s\-/]+/).some((word) => word.startsWith(q))) return 1;
        if (n.includes(q)) return 2;
        return -1;
    };
    const candidates = tools
        .map((tool) => ({ tool, rank: rank(tool.name) }))
        .filter((entry) => entry.rank >= 0)
        .sort((a, b) => a.rank - b.rank || a.tool.name.localeCompare(b.tool.name))
        .slice(0, limit)
        .map((entry) => entry.tool);
    return closesOnExactName(query, candidates) ? [] : candidates;
}

function closesOnExactName(query: string, candidates: CustomerToolOption[]): boolean {
    const q = query.toLocaleLowerCase();
    return candidates.length === 1 && candidates[0].name.toLocaleLowerCase() === q;
}

/** Ersetzt das getippte `@…` durch `@Name ` und gibt die neue Schreibmarke zurück. */
export function insertMention(
    text: string,
    mention: ActiveMention,
    caret: number,
    name: string,
): { text: string; caret: number } {
    const inserted = `@${name} `;
    const after = text.slice(caret).replace(/^ /, '');
    return {
        text: `${text.slice(0, mention.start)}${inserted}${after}`,
        caret: mention.start + inserted.length,
    };
}

function mentionPositions(text: string, name: string): number[] {
    const haystack = text.toLocaleLowerCase();
    const needle = `@${name.toLocaleLowerCase()}`;
    const positions: number[] = [];
    let from = 0;
    for (;;) {
        const at = haystack.indexOf(needle, from);
        if (at < 0) return positions;
        if ((at === 0 || /\s/.test(text[at - 1])) && isBoundary(text[at + needle.length])) positions.push(at);
        from = at + 1;
    }
}

/**
 * Die Werkzeuge, die im Text als `@Name` stehen, in der Reihenfolge ihres ersten Auftretens. Ein
 * längerer Name gewinnt gegen einen kürzeren an derselben Stelle („@Karten und Gebäude" ist nicht
 * zusätzlich „@Karten").
 */
export function findMentionedTools(text: string, tools: CustomerToolOption[]): CustomerToolOption[] {
    if (!text.includes('@')) return [];
    const taken: Array<[number, number]> = [];
    const found: Array<{ tool: CustomerToolOption; at: number }> = [];
    for (const tool of [...tools].sort((a, b) => b.name.length - a.name.length)) {
        const at = mentionPositions(text, tool.name).find(
            (pos) => !taken.some(([start, end]) => pos >= start && pos < end),
        );
        if (at === undefined) continue;
        taken.push([at, at + tool.name.length + 1]);
        found.push({ tool, at });
    }
    return found.sort((a, b) => a.at - b.at).map((entry) => entry.tool);
}

/** Nimmt jedes `@Name` dieses Werkzeugs aus dem Text, samt einem folgenden Leerzeichen. */
export function removeMention(text: string, name: string): string {
    let result = text;
    for (const at of mentionPositions(text, name).reverse()) {
        const end = at + name.length + 1;
        result = result.slice(0, at) + result.slice(result[end] === ' ' ? end + 1 : end);
    }
    return result;
}
